import api from './api';

export const analyticsService = {
  /**
   * Fetch available projects for analytics with timeframes
   * Fetches projects, gets their timeframes, and sorts by end date (newest first)
   */
  async getProjects() {
    try {
      const response = await api.get('/api/simulation/projects/bulk-timeframes');
      const projectsWithTimeframes = response.data.data || [];

      // Sort projects by end date (newest first)
      const sortedProjects = [...projectsWithTimeframes].sort((a, b) => {
        const dateA = a.timeframe?.end ? new Date(a.timeframe.end) : new Date(0);
        const dateB = b.timeframe?.end ? new Date(b.timeframe.end) : new Date(0);
        return dateB - dateA;
      });

      return {
        success: true,
        data: sortedProjects.map(project => project.name)
      };
    } catch (error) {
      console.error('Error fetching analytics projects:', error);
      throw error;
    }
  },

  /**
   * Get the default project (most recent by end date)
   */
  async getDefaultProject() {
    try {
      const projectsResponse = await this.getProjects();
      if (projectsResponse.success && projectsResponse.data.length > 0) {
        return {
          success: true,
          data: projectsResponse.data[0] // First project is most recent
        };
      }
      return {
        success: false,
        data: null
      };
    } catch (error) {
      console.error('Error getting default project:', error);
      return {
        success: false,
        data: null
      };
    }
  },

  /**
   * Fetch analytics data for a specific project
   */
  async getProjectAnalytics(projectId) {
    try {
      console.log('Getting analytics for project:', projectId);
      const response = await api.get(`/api/analytics/projects/${encodeURIComponent(projectId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project analytics:', error);
      throw error;
    }
  },

  /**
   * Fetch key metrics for analytics dashboard
   */
  async getKeyMetrics(projectId) {
    try {
      console.log('Getting key metrics for project:', projectId);
      const response = await api.get(`/api/analytics/projects/${encodeURIComponent(projectId)}/metrics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching key metrics:', error);
      throw error;
    }
  },

  /**
   * Fetch CO2 breakdown data for analytics
   */
  async getCO2Breakdown(projectId) {
    try {
      console.log('Getting CO2 breakdown for project:', projectId);
      const response = await api.get(`/api/analytics/projects/${encodeURIComponent(projectId)}/co2-breakdown`);
      return response.data;
    } catch (error) {
      console.error('Error fetching CO2 breakdown:', error);
      throw error;
    }
  },

  /**
   * Fetch project timeframe data
   * Uses the simulation endpoint which provides timeframe information
   */
  async getProjectTimeframe(projectId) {
    try {
      console.log('Getting timeframe for project:', projectId);
      const response = await api.get(`/api/simulation/projects/${encodeURIComponent(projectId)}`);
      return {
        success: true,
        data: response.data.timeframe
      };
    } catch (error) {
      console.error('Error fetching project timeframe:', error);
      throw error;
    }
  },

  /**
   * Fetch construction layer areas data for a project
   */
  async getLayerAreas(projectId) {
    try {
      console.log('Getting layer areas for project:', projectId);
      const response = await api.get(`/api/simulation/projects/${encodeURIComponent(projectId)}/layer-areas`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching layer areas:', error);
      // Return success: false for 404 (no layer areas found) to handle gracefully
      if (error.response && error.response.status === 404) {
        return {
          success: false,
          data: null,
          message: 'No layer areas found for this project'
        };
      }
      throw error;
    }
  }
};
